import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, BarChart3, Brain, LineChart, Calculator, ShieldAlert, ClipboardList, TrendingUp } from 'lucide-react';
export function ProfessionalComparison() {
  const comparisons = [{
    situation: 'Usás el xG de Sofascore pero no entendés si es confiable',
    without: 'Lo usás como número sin contexto',
    with: 'Entendés sus limitaciones y usás PSxG, xT y Field Tilt'
  }, {
    situation: 'Querés saber si tu modelo de análisis funciona estadísticamente',
    without: 'Evaluás por resultado individual',
    with: 'Validación estadística con muestra, ROI y calibración'
  }, {
    situation: 'Analizás el estilo de un equipo manualmente',
    without: 'Clasificás intuitivamente',
    with: 'Clustering en 6 arquetipos con estadísticas verificables'
  }, {
    situation: 'Necesitás calcular la probabilidad exacta de un resultado',
    without: 'Estimás intuitivamente',
    with: 'Modelo de Poisson con calculadora automática en Excel'
  }, {
    situation: 'Perdés 5 seguidas y querés seguir con cabeza fría',
    without: 'El tilt te gana eventualmente',
    with: '4 tipos de tilt con protocolos específicos'
  }, {
    situation: 'Buscás el mejor registro de tus apuestas',
    without: 'Plantilla manual en PDF',
    with: 'Tracker Elite con dashboard automático y 8 hojas interconectadas'
  }];
  const modules = [{
    icon: <BarChart3 className="w-6 h-6" data-id="element-247" />,
    title: 'Tomo Elite 1 — Análisis Estadístico Avanzado',
    color: 'text-amber-300',
    bgColor: 'bg-amber-500/20',
    items: 'xG avanzado y sus limitaciones · PSxG para evaluar porteros · Expected Threat (xT) · Goals Added (G+) · Field Tilt · Modelo de Poisson paso a paso · Clustering táctico en 6 arquetipos'
  }, {
    icon: <Brain className="w-6 h-6" data-id="element-248" />,
    title: 'Tomo Elite 2 — Psicología de Alto Rendimiento',
    color: 'text-yellow-300',
    bgColor: 'bg-yellow-500/20',
    items: 'Marco de decisión bajo presión (Sistema 1 vs. Sistema 2) · Rutinas del apostador élite · Gestión avanzada de los 4 tipos de tilt · Pensamiento probabilístico profundo · Mentalidad de portafolio'
  }, {
    icon: <LineChart className="w-6 h-6" data-id="element-249" />,
    title: 'Tomo Elite 3 — Enfoques de Investigación',
    color: 'text-orange-300',
    bgColor: 'bg-orange-500/20',
    items: 'Enfoque cuantitativo y cualitativo · Enfoque mixto — el más completo · Diseños de investigación para análisis de partidos · Validez y confiabilidad · Análisis crítico de tipsters'
  }, {
    icon: <Calculator className="w-6 h-6" data-id="element-250" />,
    title: 'Tracker Elite en Excel',
    color: 'text-emerald-300',
    bgColor: 'bg-emerald-500/20',
    items: 'Dashboard automático con todos los KPIs · Calculadora de value con modelo de Poisson integrado · Análisis por mercado, fase y estado emocional · Curva de evolución del bankroll'
  }, {
    icon: <ShieldAlert className="w-6 h-6" data-id="element-251" />,
    title: 'Bonus Elite 1 — Pensamiento Probabilístico',
    color: 'text-red-300',
    bgColor: 'bg-red-500/20',
    items: '12 ejercicios para entrenar el pensamiento probabilístico profundo · Detección de sesgos cognitivos · Marco mental del apostador de largo plazo'
  }, {
    icon: <ClipboardList className="w-6 h-6" data-id="element-252" />,
    title: 'Bonus Elite 2 — Plantilla con Poisson',
    color: 'text-purple-300',
    bgColor: 'bg-purple-500/20',
    items: 'Plantilla de análisis avanzado con modelo de Poisson integrado · Cálculo automático de probabilidades por marcador · Comparativa con cuota del mercado'
  }, {
    icon: <TrendingUp className="w-6 h-6" data-id="element-253" />,
    title: 'Bonus Elite 3 — Base de Datos Histórica',
    color: 'text-amber-200',
    bgColor: 'bg-amber-600/20',
    items: 'Tendencias históricas del Mundial 1990-2022 · Patrones por fase del torneo · Promedios de goles, BTTS y corners por edición · Lecciones aplicables a 2026'
  }];
  return <section className="py-20 bg-black" data-id="element-254">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-255">
        <div className="text-center mb-12" data-id="element-256">
          <h2 className="md:text-4xl font-extrabold text-white mb-4 text-[20px]" data-id="element-257">
            🎯 ¿QUÉ AGREGA EL "ELITE BETTING SYSTEM"?
          </h2>
          <p className="text-zinc-200 text-[14px]" data-id="element-258">
            La diferencia entre apostar con sistema y apostar con el sistema más
            sofisticado disponible.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="mb-16 overflow-x-auto pb-4" data-id="element-259">
          <div className="min-w-[800px]" data-id="element-260">
            <div className="grid grid-cols-3 gap-4 mb-4 px-4" data-id="element-261">
              <div className="font-bold text-amber-300 uppercase text-sm tracking-wider" data-id="element-262">
                Situación real
              </div>
              <div className="font-bold text-red-400 uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-263">
                <X className="w-4 h-4" data-id="element-264" /> Sin Elite System
              </div>
              <div className="font-bold text-emerald-400 uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-265">
                <Check className="w-4 h-4" data-id="element-266" /> Con Elite System
              </div>
            </div>

            <div className="space-y-3" data-id="element-267">
              {comparisons.map((row, i) => <div key={i} className="grid grid-cols-3 gap-4 bg-zinc-900/60 rounded-xl p-4 items-center border border-amber-900/30" data-id="element-268">
                  <div className="font-medium text-white" data-id="element-269">{row.situation}</div>
                  <div className="text-zinc-300 text-sm" data-id="element-270">{row.without}</div>
                  <div className="text-emerald-200 font-medium text-sm bg-emerald-500/15 p-2 rounded-lg border border-emerald-500/30" data-id="element-271">
                    {row.with}
                  </div>
                </div>)}
            </div>
          </div>
          <p className="text-center text-sm text-amber-500 mt-4 md:hidden" data-id="element-272">
            ← Scroll to see full table →
          </p>
        </div>

        {/* Modules Grid */}
        <div className="bg-zinc-900 rounded-3xl p-8 md:p-12 text-white shadow-xl border border-amber-900/40" data-id="element-273">
          <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-white" data-id="element-274">
            <span className="bg-emerald-500 text-white p-1 rounded-full" data-id="element-275">
              <Check className="w-5 h-5" data-id="element-276" />
            </span>
            EL ELITE SYSTEM INCLUYE:
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-277">
            {modules.map((mod, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="bg-black/50 rounded-2xl p-6 border border-amber-900/30" data-id="element-278">
                <div className="flex items-center gap-3 mb-4" data-id="element-279">
                  <div className={`p-2 rounded-lg ${mod.bgColor} ${mod.color}`} data-id="element-280">
                    {mod.icon}
                  </div>
                  <h4 className="font-bold text-lg text-white" data-id="element-281">{mod.title}</h4>
                </div>
                <p className="text-zinc-200 text-sm leading-relaxed" data-id="element-282">
                  {mod.items}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}